// Single source of truth for tunable constants (plan §3.10 / §6).
// Keep ALL magic numbers here so balancing never means hunting through systems.

export const ARENA = {
  width: 3200,
  height: 3200,
} as const;

/** Pixel-art magnification of the world. Integer keeps pixels crisp. */
export const ZOOM = 2;

/** Uniform spatial-hash cell size (~2x average entity diameter). */
export const HASH_CELL = 48;

/** Largest enemy radius in play; pads broad-phase queries so big bodies (bosses)
 *  aren't missed when their center sits in a neighboring cell. */
export const MAX_ENEMY_RADIUS = 48;

/** Enemies push apart so chasers don't stack into an overlapping column (plan §9-F).
 *  maxStep caps the per-tick displacement so dense crowds can't explode/oscillate. */
export const SEPARATION = { strength: 0.5, maxStep: 4 } as const;

/** Half-buried boulders: static circular obstacles the player/enemies slide around and
 *  projectiles are blocked by. Seeded per run. */
export const BOULDER = {
  count: 54,
  radiusMin: 16,
  radiusMax: 30,
  maxRadius: 30, // query pad (= radiusMax)
  clearCenterPx: 260, // keep the player's spawn area clear
  minSpacingPx: 80, // don't clump
} as const;

/** Rare water puddles: passable, but stepping in makes you "wet" (slowed). Seeded.
 *  Sizes + shapes vary per puddle so no two look the same. */
export const PUDDLE = {
  count: 16,
  radiusMin: 20,
  radiusMax: 58,
  maxRadius: 58, // query pad (= radiusMax)
  clearCenterPx: 240,
  minSpacingPx: 150,
  shapes: 4, // number of puddle sprite variants
} as const;

/** "Wet" status from puddles: slows movement, lingers after leaving the water. */
export const WET = {
  durationSec: 4, // how long the slow lasts after leaving a puddle
  slowMul: 0.5, // movement speed multiplier while wet
} as const;

/** Healing altar: a fixed platform near a corner. Standing on it (when ready) heals a
 *  fraction of max HP, then it goes on cooldown. */
export const ALTAR = {
  x: 560,
  y: 560,
  radius: 16, // sprite + glow + stand area all scale from this
  healFrac: 0.3,
  cooldownSec: 30,
} as const;

// --- Active abilities (unlock one every 5 levels; tap the hotkey to cast) ---
export interface AbilityDef {
  readonly key: string;
  /** Runtime behavior may be shared by differently themed arena powers. */
  readonly effect?: AbilityEffectKey;
  readonly name: string;
  readonly unlockLevel: number;
  readonly hotkey: string; // KeyboardEvent.code
  readonly label: string; // shown on the HUD slot
  readonly cooldownSec: number;
  readonly durationSec: number; // 0 = instant
  readonly color: number; // glow / fx color
  readonly desc: string;
}

export type AbilityEffectKey =
  | 'berserk'
  | 'cryo'
  | 'transmute'
  | 'aegis'
  | 'blackhole'
  | 'tempest'
  | 'meltdown'
  | 'overload';

export const ABILITIES: ReadonlyArray<AbilityDef> = [
  { key: 'berserk',   name: 'Berserk',    unlockLevel: 5,  hotkey: 'KeyA', label: 'A', cooldownSec: 25, durationSec: 5,   color: 0xff3030, desc: '3x damage, 5x point-blank, invulnerable' },
  { key: 'cryo',      name: 'Cryo Nova',  unlockLevel: 10, hotkey: 'KeyS', label: 'S', cooldownSec: 30, durationSec: 0,   color: 0x4fd6ff, desc: 'Freeze all nearby enemies' },
  { key: 'transmute', name: 'Transmute',  unlockLevel: 15, hotkey: 'KeyD', label: 'D', cooldownSec: 35, durationSec: 0,   color: 0xff4dff, desc: 'Arcane blast around you' },
  { key: 'aegis',     name: 'Aegis',      unlockLevel: 20, hotkey: 'KeyF', label: 'F', cooldownSec: 30, durationSec: 5,   color: 0xffd34d, desc: 'Shield + heal + knockback' },
  { key: 'blackhole', name: 'Black Hole', unlockLevel: 25, hotkey: 'KeyZ', label: 'Z', cooldownSec: 40, durationSec: 1.5, color: 0x9b5cff, desc: 'Pull enemies in, then detonate' },
  { key: 'tempest',   name: 'Tempest',    unlockLevel: 30, hotkey: 'KeyX', label: 'X', cooldownSec: 30, durationSec: 6,   color: 0x59ffd0, desc: 'Speed surge + shock trail' },
  { key: 'meltdown',  name: 'Meltdown',   unlockLevel: 35, hotkey: 'KeyC', label: 'C', cooldownSec: 35, durationSec: 0,   color: 0xff7a2a, desc: 'Ignite all nearby enemies' },
  { key: 'overload',  name: 'Overload',   unlockLevel: 40, hotkey: 'KeyV', label: 'V', cooldownSec: 40, durationSec: 3,   color: 0xffe14d, desc: 'Lightning storm' },
];

/** Per-ability tuning (all in one place per plan §6). */
export const ABILITY = {
  berserkDmgMul: 3,
  berserkCloseMul: 5,
  berserkCloseRadius: 90,
  cryoRadius: 640,
  cryoSlowSec: 4,
  cryoSlowMul: 0.12,
  transmuteRadius: 230,
  transmuteDamage: 240,
  aegisHeal: 40,
  aegisKnockRadius: 175,
  aegisKnockback: 150,
  blackholeRadius: 620,
  blackholePullPerSec: 460,
  blackholeBoomRadius: 210,
  blackholeDamage: 220,
  tempestSpeedMul: 2,
  tempestTickSec: 0.15,
  tempestRadius: 48,
  tempestDamage: 26,
  meltdownRadius: 640,
  meltdownBurnSec: 5,
  meltdownBurnDps: 65,
  overloadTickSec: 0.22, // strikes more often
  overloadTargets: 22, // a storm - hits a big chunk of the nearby swarm each strike
  overloadRadius: 620,
  overloadDamage: 120,
} as const;

/** Default targeting viewport half-extents (world px); main overrides on resize so
 *  weapons only target on-screen enemies. */
export const VIEW = { defaultHalfW: 380, defaultHalfH: 210 } as const;

/** Pre-allocated pool capacities (objects created once at load - no GC churn). */
export const POOL_CAP = {
  enemies: 900, // absolute ceiling - kept high so the late-game swarm peak stays intense
  projectiles: 2000,
  motes: 1200,
  nectars: 48,
  healthOrbs: 32,
} as const;

export const PLAYER = {
  maxHp: 100,
  speedPerSec: 180,
  radius: 10,
  pickupRadius: 16, // touch radius to actually collect a mote
  iFramesSec: 0.5,
  regenPerSec: 0,
} as const;

/** XP mote magnet: motes within range glide toward the player. */
export const MAGNET = {
  baseRange: 95,
  pullSpeedPerSec: 320,
} as const;

/** Base critical hit (boosted by the Lucky Vial passive). */
export const CRIT = {
  baseChance: 0.05,
  baseMult: 2.0,
} as const;

/** Level-up draft sizing. */
export const DRAFT = {
  choices: 3,
  maxWeapons: 6,
  maxPassives: 6,
} as const;

/** XP needed to go from `level` to `level+1`. */
export const xpToNext = (level: number): number =>
  Math.floor(8 + level * 6 + level * level * 0.6);

// --- Enemies ---
export type EnemyKind = 'chase' | 'weave' | 'charge' | 'split' | 'ranged' | 'summon' | 'stationary';

/** Declarative extra behavior for a creature. Keeping these rules on the creature data
 * lets levels introduce new families without simulation code depending on sprite names. */
export interface EnemySpawnRule {
  readonly enemyKey: string;
  readonly count: number;
}

export interface EnemySummonRule extends EnemySpawnRule {
  readonly cooldownSec: number;
}

export interface EnemyRangedAttack {
  readonly rangePx: number;
  readonly sprite?: string;
  readonly color?: number;
  readonly damage?: number;
  readonly speed?: number;
  readonly radius?: number;
  readonly lifeSec?: number;
  readonly bounces?: number;
  /** A direct chaser that fires, rather than the default mortar-style standoff. */
  readonly chaseWhileFiring?: boolean;
}

export interface EnemyBloomEmitter {
  readonly cooldownSec: number;
  readonly wallCount: number;
}

export interface EnemyRailEmitter {
  readonly cooldownSec: number;
  readonly railCount: number;
  /** Re-aim any existing lanes at the emitter before it adds its own rail. */
  readonly rerouteActive?: boolean;
}

export interface EnemyMagnetEmitter {
  readonly radiusPx: number;
  readonly pullPerSec: number;
}

export interface EnemyDeathEffect {
  readonly dropsNectar?: boolean;
  readonly bloomWallCount?: number;
}

/** Pre-allocated pool capacity for enemy projectiles (mortar shots). */
export const ENEMY_SHOT = {
  damage: 12,
  speed: 220,
  radius: 5,
  lifeSec: 3.2,
  color: 0xb6ff5a,
  cooldownSec: 2.2,
  rangePx: 460,
  poolCap: 400,
} as const;

export interface EnemyType {
  readonly key: string;
  readonly hp: number;
  readonly contactDmg: number;
  readonly speedPerSec: number;
  readonly radius: number;
  readonly xp: number;
  readonly color: number;
  readonly kind: EnemyKind;
  readonly sprite: string; // pixel-art texture key (see render/pixelArt.ts)
  readonly split?: EnemySpawnRule;
  readonly summon?: EnemySummonRule;
  readonly rangedAttack?: EnemyRangedAttack;
  readonly bloomEmitter?: EnemyBloomEmitter;
  readonly railEmitter?: EnemyRailEmitter;
  readonly magnetEmitter?: EnemyMagnetEmitter;
  readonly onDeath?: EnemyDeathEffect;
}

export const ENEMY_TYPES: Record<string, EnemyType> = {
  // Fodder horde.
  slime: { key: 'slime', hp: 14, contactDmg: 5, speedPerSec: 52, radius: 9, xp: 1, color: 0xff5a6a, kind: 'chase', sprite: 'slime' },
  // Fast, fragile swarmer.
  spore: { key: 'spore', hp: 8, contactDmg: 4, speedPerSec: 104, radius: 6, xp: 1, color: 0xff9a3a, kind: 'chase', sprite: 'spore' },
  // Fast, small, clustered.
  glass: { key: 'glass', hp: 24, contactDmg: 6, speedPerSec: 90, radius: 7, xp: 2, color: 0x8ab4ff, kind: 'chase', sprite: 'glass' },
  // Weaving, medium HP.
  wraith: { key: 'wraith', hp: 42, contactDmg: 12, speedPerSec: 70, radius: 10, xp: 4, color: 0xc06bff, kind: 'weave', sprite: 'wraith' },
  // Slow tank that splits into slimes on death.
  vat: { key: 'vat', hp: 140, contactDmg: 14, speedPerSec: 30, radius: 16, xp: 6, color: 0x6ad06a, kind: 'split', sprite: 'vat', split: { enemyKey: 'slime', count: 3 } },
  // Elite that periodically charges the player.
  scorpion: { key: 'scorpion', hp: 280, contactDmg: 20, speedPerSec: 58, radius: 13, xp: 16, color: 0xff2a2a, kind: 'charge', sprite: 'scorpion' },
  // Ranged: keeps its distance and lobs spore shots.
  mortar: { key: 'mortar', hp: 70, contactDmg: 10, speedPerSec: 42, radius: 9, xp: 5, color: 0xb6ff5a, kind: 'ranged', sprite: 'mortar', rangedAttack: { rangePx: ENEMY_SHOT.rangePx } },
};

/** Bosses + the Catalyst run-ender. Spawned by the director's timed schedule. */
export const BOSS_TYPES: Record<string, EnemyType> = {
  flask: { key: 'flask', hp: 2400, contactDmg: 26, speedPerSec: 34, radius: 34, xp: 120, color: 0x6ad06a, kind: 'summon', sprite: 'flask', summon: { enemyKey: 'slime', count: 4, cooldownSec: 3 } },
  construct: { key: 'construct', hp: 4600, contactDmg: 34, speedPerSec: 96, radius: 30, xp: 200, color: 0xc8c8f0, kind: 'chase', sprite: 'construct' },
  catalyst: { key: 'catalyst', hp: 10000, contactDmg: 60, speedPerSec: 124, radius: 26, xp: 300, color: 0xff4dff, kind: 'charge', sprite: 'catalyst' },
};

/** Flask boss periodically summons slimes. */
export const SUMMON = { cooldownSec: 3, count: 4 } as const;

// --- Spawning / difficulty director ---
export const SPAWN = {
  /** Fixed distance from the player to spawn (off-screen, screen-size independent → deterministic). */
  distancePx: 720,
  distanceJitterPx: 140,
  /** Hard cap on simultaneously active enemies (protects FPS). */
  maxActive: 720,
  /** Max enemies spawned in a single tick (avoids a frame spike). */
  maxPerTick: 12,
} as const;

export const DIFFICULTY = {
  /** Spawn rate ramps from base → max over rampToMaxSec.
   *  Early rate trimmed ~10%; late (max) rate kept full so the end-game stays swarmy. */
  baseSpawnPerSec: 2.7,
  maxSpawnPerSec: 38,
  rampToMaxSec: 480,
  /** Per-minute multiplicative scaling applied to spawned enemies. */
  hpScalePerMin: 0.1,
  dmgScalePerMin: 0.06,
} as const;

/** Shared difficulty modes chosen on the start screen. A level may layer narrowly
 * scoped overrides over these defaults. Easy runs are never submitted to the global
 * leaderboard, so the competitive ladder stays fair. */
export interface DifficultyMode {
  readonly spawnMul: number; // monster spawn rate + event bursts
  readonly xpMul: number; // XP gained
  readonly dmgTakenMul: number; // damage the player takes
  readonly enemyHpMul: number; // HP multiplier for all enemies, including bosses
  readonly hazardForceMul: number; // movement force from arena hazards
  readonly healthOrbDropChance: number; // chance per ordinary enemy; bosses are guaranteed
  readonly healthOrbHealFrac: number; // fraction of max HP restored per pickup
  readonly ranked: boolean; // counts on the global leaderboard
}
export type DifficultyKey = 'normal' | 'easy';
export const DIFFICULTY_MODES: Record<DifficultyKey, DifficultyMode> = {
  normal: {
    spawnMul: 1,
    xpMul: 1,
    dmgTakenMul: 1,
    enemyHpMul: 1,
    hazardForceMul: 1,
    healthOrbDropChance: 0,
    healthOrbHealFrac: 0,
    ranked: true,
  },
  easy: {
    spawnMul: 0.5,
    xpMul: 2,
    dmgTakenMul: 0.5,
    enemyHpMul: 1,
    hazardForceMul: 1,
    healthOrbDropChance: 0,
    healthOrbHealFrac: 0,
    ranked: false,
  },
};

/** Enemy types unlock over time; the director picks a weighted-random unlocked type.
 *  `t` is the unlock time (seconds), `w` the spawn weight once unlocked. */
export const TIER_UNLOCK: ReadonlyArray<{ t: number; key: string; w: number }> = [
  { t: 0, key: 'slime', w: 10 },
  { t: 0, key: 'spore', w: 6 },
  { t: 90, key: 'glass', w: 6 },
  { t: 210, key: 'wraith', w: 5 },
  { t: 330, key: 'vat', w: 3 },
  { t: 360, key: 'mortar', w: 2 },
  { t: 450, key: 'scorpion', w: 1 },
];

/** Timed director schedule: one-shot bosses + named escalation events (burst spawns).
 *  Fires once when the run clock crosses `t`. */
export interface ScheduleEntry {
  t: number;
  kind: 'boss' | 'event' | 'bloom' | 'rail';
  banner: string;
  bossKey?: string;
  count?: number; // bosses to spawn (Catalyst spawns several)
  burst?: number; // event: enemies to spawn at once
  bloomWallCount?: number; // bloom: temporary arena walls to telegraph and grow
  gravityRailCount?: number; // rail: directional lanes to telegraph and activate
}

export const SCHEDULE: ReadonlyArray<ScheduleEntry> = [
  { t: 120, kind: 'event', banner: 'THE SURGE', burst: 36 },
  { t: 300, kind: 'boss', bossKey: 'flask', banner: 'OVERFLOWING FLASK' },
  { t: 480, kind: 'event', banner: 'CONTAINMENT BREACH', burst: 70 },
  { t: 600, kind: 'boss', bossKey: 'construct', banner: 'QUICKSILVER CONSTRUCT' },
  { t: 780, kind: 'event', banner: 'THE SWARM', burst: 110 },
  { t: 900, kind: 'boss', bossKey: 'catalyst', banner: 'THE CATALYST', count: 1 },
];

/** Reaching this run time = victory (the Catalyst spawns to end lingering runs). */
export const RUN_LENGTH_SEC = 900;

/** Charging enemy (scorpion) tuning. */
export const CHARGE = {
  cooldownSec: 3.2,
  windupSec: 0.5,
  dashSec: 0.45,
  dashSpeedMul: 4.5,
  triggerRange: 320,
} as const;

/** Weaving enemy (wraith) tuning. */
export const WEAVE = { ampPerSec: 90, freq: 4 } as const;

// --- Weapons ---
export const ACID_VIAL = {
  damage: 12,
  cooldownSec: 1.1,
  projectileSpeed: 340,
  projectileRadius: 4,
  rangePx: 480,
  lifeSec: 1.4,
  color: 0x7cfc00,
} as const;

// --- Pickups ---
export const MOTE = {
  radius: 4,
  color: 0x59ffd0,
} as const;

export const HIT_FLASH_SEC = 0.08;

export const COLORS = {
  bg: 0x15131f,
  grid: 0x231f33,
  player: 0xffd9a0,
  playerOutline: 0x1a1726,
  hitFlash: 0xffffff,
} as const;
